/**
 * Supervision par groupe de jeu (NationsGlory : Nova, Iris...) : fonctions pures, sans I/O ni store.
 *
 * Nova et Iris partagent le même Pterodactyl ; chaque satellite ne sert que les serveurs de son groupe
 * (variable RECUBE_GROUP) et répond 404 `SERVER_OUT_OF_SCOPE` pour les autres. Ces fonctions décident
 * ce que la page Serveur montre sur une erreur : jamais de fausse console ni de logs simulés quand
 * l'erreur tient au périmètre, au groupe ou à la cible. Un jeu sans groupe (Paladium) garde le mode
 * démo d'avant.
 */
import type { ProjectGroupDTO, ProjectGroupsEntryDTO } from '@/services/api.service';
import { isMemberGroup } from '@/utils/gameGroups';

/** 404 du satellite : serveur d'un autre groupe de jeu. */
export const SERVER_OUT_OF_SCOPE = 'SERVER_OUT_OF_SCOPE';
/** 503 du satellite : liste des serveurs du groupe illisible (Pterodactyl injoignable). */
export const SERVER_SCOPE_UNAVAILABLE = 'SERVER_SCOPE_UNAVAILABLE';

/**
 * Résultat d'un appel du service API. `request()` renvoie aussi `status` et `code`, que les
 * signatures des méthodes ne déclarent pas toujours : ce type les rend lisibles sans conversion.
 */
export interface ApiOutcome {
  error?: string;
  status?: number;
  code?: string;
}

export type ServerFailureKind = 'scope' | 'group' | 'unreachable' | 'notFound' | 'error';

export interface ServerFailure {
  kind: ServerFailureKind;
  /** Message du backend (passerelle, relais ou satellite), qui nomme déjà le groupe. */
  message: string;
  status?: number;
  code?: string;
}

/**
 * Erreur de chargement d'un serveur (détail ou ticket console).
 *
 * @param noDemo le jeu a des groupes (NationsGlory), qu'un groupe parte ou non avec la requête
 *               (X-Game-Group) : aucune erreur ne doit alors tomber en mode démo
 * @returns null : pas d'erreur, ou erreur à traiter comme avant (mode démo), ce qui n'arrive que
 *          pour un jeu sans groupes (Paladium) et hors codes de périmètre ou de groupe ; sinon la
 *          panne à afficher.
 */
export function classifyServerFailure(res: ApiOutcome, noDemo: boolean): ServerFailure | null {
  if (!res.error) return null;
  const base = { message: res.error, status: res.status, code: res.code };
  const code = res.code ?? '';
  // Codes de périmètre et de groupe : jamais de démo, groupe actif ou pas (un en-tête perdu fait lire
  // la racine, dont le satellite peut quand même filtrer par groupe).
  if (code === SERVER_OUT_OF_SCOPE) return { kind: 'scope', ...base };
  if (code.startsWith('GAME_GROUP_')) return { kind: 'group', ...base };
  if (code === SERVER_SCOPE_UNAVAILABLE) return { kind: 'unreachable', ...base };
  if (!noDemo) return null;
  const status = res.status ?? 0;
  if (status === 502 || status === 503 || status === 504) return { kind: 'unreachable', ...base };
  if (status === 404) return { kind: 'notFound', ...base };
  return { kind: 'error', ...base };
}

/** Titre et explication de la panne, nommés par le groupe consulté. */
export function serverFailureText(
  failure: ServerFailure,
  groupLabel: string | null,
  identifier: string,
): { title: string; detail: string } {
  const where = groupLabel ? ` ${groupLabel}` : '';
  switch (failure.kind) {
    case 'scope':
      return {
        title: groupLabel ? `Ce serveur n'est pas dans ${groupLabel}` : "Ce serveur n'est pas dans ce groupe de jeu",
        detail: `« ${identifier} » appartient à un autre groupe de jeu (variable RECUBE_GROUP du serveur). `
          + 'Choisis son groupe pour ouvrir sa console.',
      };
    case 'group':
      return { title: 'Groupe de jeu refusé', detail: failure.message };
    case 'unreachable':
      return { title: `Satellite${where} injoignable`, detail: failure.message };
    case 'notFound':
      return { title: groupLabel ? `Serveur introuvable dans ${groupLabel}` : 'Serveur introuvable', detail: failure.message };
    default:
      return { title: 'Impossible de charger le serveur', detail: failure.message };
  }
}

/**
 * Groupes vers lesquels la page propose de basculer : disponibles, dans l'affectation du membre du
 * staff, et différents du groupe consulté.
 * Rien pour une panne du satellite (réessayer a plus de sens) ; pour un refus de groupe, la
 * passerelle a déjà dit lequel ne va pas.
 */
export function switchTargets(
  failure: ServerFailure,
  entry: ProjectGroupsEntryDTO | undefined,
  activeKey: string | null,
): ProjectGroupDTO[] {
  if (!entry || (failure.kind !== 'scope' && failure.kind !== 'group' && failure.kind !== 'notFound')) return [];
  return entry.groups.filter((g) => g.available && isMemberGroup(g) && g.key !== activeKey);
}

/** Une panne du satellite ou une erreur inattendue peut se réessayer ; un refus de périmètre non. */
export function canRetry(failure: ServerFailure): boolean {
  return failure.kind === 'unreachable' || failure.kind === 'error';
}

/** « Nova », « Nova et Iris », « Nova, Iris et Zeus ». */
export function joinGroupLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]}`;
}

/** Première erreur d'une série d'appels, null si tous ont réussi. */
export function firstError(...results: ApiOutcome[]): string | null {
  for (const r of results) if (r.error) return r.error;
  return null;
}
